import {
  getAccessSystemVouchers,
  saveAccessSystemVoucher,
  updateAccessSystemVoucher,
} from "app/services/api/accessSystemSettings";

const CHANGE_EVENT = "dsok-access-system-vouchers-changed";

/** In-memory snapshot; refreshed from GET / POST responses. */
let clientSnapshot = null;

function normalizeListResponse(raw) {
  if (!raw) {
    return { voucherSeries: [] };
  }
  if (raw.success === true && Array.isArray(raw.data)) {
    return { voucherSeries: raw.data };
  }
  if (raw && Array.isArray(raw.voucherSeries)) {
    return { voucherSeries: raw.voucherSeries };
  }
  if (Array.isArray(raw)) {
    return { voucherSeries: raw };
  }
  return { voucherSeries: [] };
}

/** Unwrap `{ success, data }` from POST responses when present. */
function unwrapVoucherPostResponse(raw) {
  if (raw == null) {
    return raw;
  }
  if (
    typeof raw === "object" &&
    raw.success === true &&
    raw.data != null &&
    typeof raw.data === "object"
  ) {
    return raw.data;
  }
  return raw;
}

/**
 * Current voucher document `{ voucherSeries: [...] }`.
 * Empty until {@link loadVouchersFromApi} completes or after {@link persistVoucherSeries}.
 */
export function getVouchersData() {
  return clientSnapshot || { voucherSeries: [] };
}

/**
 * Load vouchers from backend for forms (wide page size so edit/detail can resolve by id).
 */
export async function loadVouchersFromApi() {
  const raw = await getAccessSystemVouchers({ page: 1, limit: 500 });
  clientSnapshot = normalizeListResponse(raw);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: clientSnapshot }));
  }
  return clientSnapshot;
}

/**
 * Save one voucher series: POST when no id (create), PUT when id present (update).
 * Merges into local snapshot and notifies listeners.
 */
export async function persistVoucherSeries(series) {
  const sid =
    series != null &&
    series.id != null &&
    String(series.id).trim() !== ""
      ? String(series.id).trim()
      : "";
  const raw = sid
    ? await updateAccessSystemVoucher(sid, series)
    : await saveAccessSystemVoucher(series);
  const entity = unwrapVoucherPostResponse(raw);
  const base = getVouchersData();
  const list = [...(base.voucherSeries || [])];

  if (entity != null && typeof entity === "object" && Array.isArray(entity.voucherSeries)) {
    clientSnapshot = { voucherSeries: entity.voucherSeries };
  } else if (
    entity != null &&
    typeof entity === "object" &&
    !Array.isArray(entity) &&
    entity.id != null &&
    !Array.isArray(entity.voucherSeries)
  ) {
    const id = String(entity.id);
    const idx = list.findIndex((s) => String(s.id) === id);
    if (idx >= 0) {
      list[idx] = entity;
    } else {
      list.push(entity);
    }
    clientSnapshot = { voucherSeries: list };
  } else if (sid) {
    const idx = list.findIndex((s) => String(s.id) === sid);
    if (idx >= 0) {
      list[idx] = series;
    } else {
      list.push(series);
    }
    clientSnapshot = { voucherSeries: list };
  } else {
    clientSnapshot = { voucherSeries: list };
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: clientSnapshot }));
  }
  return { snapshot: clientSnapshot, apiEntity: entity };
}

export function subscribeVouchersData(listener) {
  if (typeof window === "undefined") return () => {};
  const fn = () => listener(getVouchersData());
  window.addEventListener(CHANGE_EVENT, fn);
  return () => window.removeEventListener(CHANGE_EVENT, fn);
}
